import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import {
  ADDRESS_AUTOCOMPLETE_OPTIONS,
  VAGUE_ADDRESS_MESSAGE,
  extractDestination,
  isSpecificAddress,
} from './addressValidation';

/**
 * LocationSearchBox — a Google-Maps-style address search field for DriverApp.
 *
 * Replaces the bare <Autocomplete><input/></Autocomplete> pairs that were duplicated in
 * LiveDispatch/FlagTripModal and Itinerary/FlagTripCreateModal. Those worked, but were
 * missing the behaviours a driver expects from a Maps search box:
 *
 *   1. LOCATION BIAS — suggestions are biased to a radius around the driver's current GPS
 *      fix, so typing "main" offers the Main St they are two blocks from rather than one
 *      in another state. This is the single biggest relevance win and the main reason
 *      this component exists.
 *   2. VISIBLE SELECTION STATE — a green confirmation row showing what actually resolved,
 *      with a clear (x) button. Previously the driver typed into a box and got no
 *      feedback beyond the text they themselves typed.
 *   3. CLEAR (x) BUTTON — resets the field and the selection together, so a mistyped
 *      address can't leave a stale resolved destination behind it.
 *   4. INLINE VALIDATION — a city-only pick is rejected in place with VAGUE_ADDRESS_MESSAGE
 *      instead of silently producing an unroutable destination.
 *   5. KEYBOARD-SAFE ENTER — Enter with the dropdown open must not submit the surrounding
 *      form. See the keydown handler below.
 *   6. RECENT/SAVED CHIPS — rendered inside the component rather than re-implemented at
 *      each call site.
 *
 * Deliberately NOT done: no custom-rendered suggestion list. Google's Places terms require
 * their attribution and the widget provides it; hand-rolling the dropdown to restyle it
 * would mean owning session tokens, debouncing and billing correctness for no real gain.
 * `fields` stays pinned to the Basic tier via ADDRESS_AUTOCOMPLETE_OPTIONS — see the
 * billing note in RiderApp's NewTrip.jsx; do not widen it without checking the tier.
 */

// Suggestions within this radius of the driver rank first. 50 km is wide enough to cover
// a metro service area plus its suburbs without cutting off a legitimate long dropoff:
// this is a BIAS, not a restrict, so out-of-radius results still appear, just lower.
const BIAS_RADIUS_METERS = 50000;

/** Short one-line label for a resolved destination: "123 Main St" or the full address. */
function shortLabel(place) {
  if (!place) return '';
  if (place.streetName) {
    return `${place.streetNumber ? place.streetNumber + ' ' : ''}${place.streetName}`;
  }
  return place.address || '';
}

const LocationSearchBox = ({
  googleMapsLoaded,
  currentLocation,            // { lat, lng } — biases suggestions toward the driver
  value,                      // resolved destination object, or null
  onChange,                   // (destination|null) => void
  recentPlaces = [],          // quick-pick chips (recentDestinations.getRecentDestinations)
  label = 'Dropoff address',
  placeholder = 'Search street address',
  required = false,
  disabled = false,
  autoFocus = false,
  accent = 'amber',           // 'amber' (itinerary) | 'orange' (live dispatch) | 'blue'
  // How a city-only pick is treated. The two flag-trip modals genuinely differ here and
  // this must not be flattened:
  //   strict   (LiveDispatch) — dropoff is routed work, so a vague pick is REJECTED and
  //                             no destination is set.
  //   advisory (Itinerary)    — dropoff is optional and only sharpens pricing, so a vague
  //                             pick is ACCEPTED with a hint.
  strict = true,
}) => {
  const acRef = useRef(null);
  const inputRef = useRef(null);
  // Text shown in the box. Kept separate from `value` so the driver can keep typing
  // freely after a selection without the field fighting them for control.
  const [text, setText] = useState(value ? value.address || '' : '');
  const [vague, setVague] = useState(false);
  // True when the driver left text in the box that never matched a Google place, so it
  // has no coordinates and is not a usable destination.
  const [unresolved, setUnresolved] = useState(false);

  // Static class strings per accent — Tailwind cannot see dynamically built class names,
  // so these must be written out in full to survive the production purge.
  const ACCENTS = {
    amber: {
      ring: 'focus:border-amber-500 focus:ring-amber-500',
      chip: 'border-amber-200 bg-amber-50 text-amber-700',
    },
    orange: {
      ring: 'focus:border-orange-500 focus:ring-orange-500',
      chip: 'border-orange-200 bg-orange-50 text-orange-700',
    },
    blue: {
      ring: 'focus:border-blue-500 focus:ring-blue-500',
      chip: 'border-blue-200 bg-blue-50 text-blue-700',
    },
  };
  const { ring, chip: chipTone } = ACCENTS[accent] || ACCENTS.amber;

  // A cleared/replaced selection from the parent must be reflected here, otherwise the
  // box keeps showing an address that is no longer the destination.
  useEffect(() => {
    if (!value) return;
    setText(value.address || '');
    setVague(false);
    setUnresolved(false);
  }, [value]);

  useEffect(() => {
    if (autoFocus && !disabled && inputRef.current) inputRef.current.focus();
  }, [autoFocus, disabled]);

  // Bias suggestions around the driver. Recomputed as the fix moves, but only at ~1 km
  // granularity: rebuilding the LatLngBounds on every GPS tick would reset the widget's
  // internal state mid-typing and make the dropdown flicker.
  const biasKey = useMemo(() => {
    const lat = Number(currentLocation?.lat);
    const lng = Number(currentLocation?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return `${lat.toFixed(2)},${lng.toFixed(2)}`;
  }, [currentLocation?.lat, currentLocation?.lng]);

  useEffect(() => {
    if (!acRef.current || !biasKey || !window.google?.maps) return;
    const [lat, lng] = biasKey.split(',').map(Number);
    try {
      const center = new window.google.maps.LatLng(lat, lng);
      // setBounds + strictBounds:false == "prefer these, don't exclude the rest".
      const circle = new window.google.maps.Circle({ center, radius: BIAS_RADIUS_METERS });
      acRef.current.setBounds(circle.getBounds());
      if (typeof acRef.current.setOptions === 'function') {
        acRef.current.setOptions({ strictBounds: false });
      }
    } catch {
      /* Biasing is an enhancement — an unbiased search is still a working search. */
    }
  }, [biasKey]);

  const handleLoad = useCallback((ac) => {
    acRef.current = ac;
  }, []);

  const handlePlaceChanged = useCallback(() => {
    const place = acRef.current?.getPlace();
    const resolved = extractDestination(place);
    if (!resolved) return; // still typing — no geometry yet
    const specific = isSpecificAddress(resolved);
    setVague(!specific);
    setUnresolved(false);
    // Always Google's canonical formatted_address, never the fragment the driver typed.
    setText(resolved.address || '');
    if (strict && !specific) {
      // Keep the text so the driver can refine it rather than retype from scratch, but
      // do not hand a city-only pick upstream as a routable destination.
      onChange(null);
      return;
    }
    onChange(resolved);
  }, [onChange, strict]);

  // Enter while the Places dropdown is open selects a suggestion. Without this guard the
  // same Enter also bubbles to the enclosing <form> and submits a flag trip with a
  // half-resolved destination — the driver presses Enter to pick an address and instead
  // creates the trip.
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') e.preventDefault();
  }, []);

  const handleClear = useCallback(() => {
    setText('');
    setVague(false);
    setUnresolved(false);
    onChange(null);
    inputRef.current?.focus();
  }, [onChange]);

  // Blur is the recovery path for a driver who typed a full address and tapped straight to
  // the next field without picking from the dropdown: getPlace() may still hold a
  // resolvable result. Only runs when nothing is resolved yet, so it can never overwrite a
  // good selection with a stale widget value.
  const handleBlur = useCallback(() => {
    if (value) return;
    if (acRef.current?.getPlace) handlePlaceChanged();
    // Still nothing resolved: the text in the box is whatever the driver typed, not a
    // Google place. It has no coordinates, so it cannot be routed to. Flag it rather
    // than leaving it looking like an accepted destination — that silence is what let a
    // raw "cleveland hopkins international airport" reach the trip with no location.
    setUnresolved(!acRef.current?.getPlace?.()?.geometry);
  }, [value, handlePlaceChanged]);

  const chips = useMemo(() => (Array.isArray(recentPlaces) ? recentPlaces.slice(0, 6) : []), [recentPlaces]);

  if (!googleMapsLoaded || !window.google?.maps?.places) {
    return (
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {label} {required ? '' : '(optional)'}
        </label>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
          Map not ready — address search unavailable.
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="location-search-input" className="mb-1 block text-sm font-medium text-gray-700">
        {label} {required ? '' : '(optional)'}
      </label>

      <div className="relative">
        <Autocomplete
          onLoad={handleLoad}
          onPlaceChanged={handlePlaceChanged}
          options={ADDRESS_AUTOCOMPLETE_OPTIONS}
        >
          <input
            id="location-search-input"
            ref={inputRef}
            type="text"
            inputMode="search"
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
            aria-label={label}
            aria-invalid={vague ? 'true' : undefined}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setVague(false);
              setUnresolved(false);
              // Typing over a resolved address invalidates it. Without this the driver
              // can edit the text while the old destination stays silently attached.
              if (value) onChange(null);
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className={`w-full rounded-lg border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 ${ring}`}
          />
        </Autocomplete>

        {text !== '' && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear address"
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full px-2 py-1 text-lg leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            &times;
          </button>
        )}
      </div>

      {/* Resolved-selection confirmation. The driver needs to see what the search actually
          matched, not just the characters they typed. */}
      {value && (
        <div className="mt-1 flex items-start gap-1 rounded-lg border border-green-200 bg-green-50 px-2 py-1.5 text-xs text-green-800">
          <span aria-hidden="true">✓</span>
          <span className="flex-1 break-words">{value.address}</span>
        </div>
      )}

      {/* Typed text that never matched a Google place. It has no coordinates, so it is
          not a destination no matter how complete it looks to the driver. */}
      {unresolved && !value && text.trim() !== '' && (
        <p role="alert" className="mt-1 text-xs font-medium text-red-600">
          Pick an address from the suggestions — typed text alone can't be used.
        </p>
      )}

      {vague && !unresolved && (
        strict ? (
          <p role="alert" className="mt-1 text-xs font-medium text-red-600">
            {VAGUE_ADDRESS_MESSAGE}
          </p>
        ) : (
          <p className="mt-1 text-xs text-amber-600">
            {VAGUE_ADDRESS_MESSAGE} (optional, but improves pricing accuracy)
          </p>
        )
      )}

      {chips.length > 0 && !value && (
        <div className="mt-2 flex flex-wrap gap-1">
          {chips.map((p) => (
            <button
              key={`${p.address}${p.lat}${p.lon}`}
              type="button"
              disabled={disabled}
              onClick={() => {
                setVague(false);
                setText(p.address || '');
                onChange(p);
              }}
              title={p.address}
              className={`max-w-[160px] truncate rounded-full border px-2 py-1 text-[10px] ${chipTone}`}
            >
              ★ {shortLabel(p)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationSearchBox;
